import { SupportAgent } from "@mui/icons-material";
import { Stack, Typography, Button, useTheme } from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode, useEffect, useState } from "react";
import { useSecretaryAgentContext } from "@/contexts/SecretaryAgentContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { PatchContract } from "@/services/fhir/contract/PatchContract";
import { toast } from "react-toastify";
import { ConfigureAIModal } from "./ConfigureAIModal";
import { CancelIntegrationModal } from "./CancelIntegrationModal";
import { useSessionContext } from "@/contexts/SessionContext";
import { ActivationInfoBlock } from "./ActivationInfoBlock";
import {
  getActivationResponsible,
  getProviderSlugFromContract,
} from "../helpers/activationInfo";
import { getProviderDisplayLabel } from "../helpers/aiProviderDisplay";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { hasActiveMetaWhatsAppConnection } from "src-next/modules/crm-clinical/setup/services/disconnectMetaOnFluxZapExtensionDeactivated";
import { SecretaryAgentEndpointNotice } from "./SecretaryAgentEndpointNotice";

interface SecretaryAgentCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

/**
 * Card da extensão Secretária IA.
 * Ativação segue o wizard de provedor/credencial, mas só libera com WhatsApp Business (Meta) ativo.
 */
export function SecretaryAgentCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: SecretaryAgentCardProps) {
  const { access } = useSessionContext();
  const { organization } = useOrganizationContext();
  const theme = useTheme();
  const { contractSecretaryAgent, provider, refetchContract } =
    useSecretaryAgentContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEndpointLoading, setIsEndpointLoading] = useState(true);
  const [hasActiveMetaEndpoint, setHasActiveMetaEndpoint] = useState(false);

  const isOwner = Boolean(access?.roles?.includes("owner"));
  const canActivate =
    isOwner && !isEndpointLoading && hasActiveMetaEndpoint;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsEndpointLoading(true);
      try {
        const active = await hasActiveMetaWhatsAppConnection({
          organizationId: resolveCrmOrganizationId(organization?.id),
        });
        if (!cancelled) setHasActiveMetaEndpoint(active);
      } catch {
        if (!cancelled) setHasActiveMetaEndpoint(false);
      } finally {
        if (!cancelled) setIsEndpointLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organization?.id]);

  const handleOpenModal = () => {
    if (!canActivate) return;
    setIsModalOpen(true);
  };

  const handleRemoveContract = async () => {
    await PatchContract({
      contractId: contractSecretaryAgent?.id,
      operation: "replace",
      path: "/status",
      content: "cancelled",
    });
    toast.success("Integração cancelada com sucesso");
    refetchContract();
    setIsModalOpen(false);
    setIsCancelModalOpen(false);
  };

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}

        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{ paddingBottom: 3, paddingLeft: 5, paddingRight: 5 }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {!contractSecretaryAgent && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <SecretaryAgentEndpointNotice
                isEndpointLoading={isEndpointLoading}
                hasActiveMetaEndpoint={hasActiveMetaEndpoint}
              />
            </Stack>
          )}

          {contractSecretaryAgent && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <ActivationInfoBlock
                responsible={getActivationResponsible(
                  contractSecretaryAgent,
                  "secretary_agent"
                )}
                providerLabel={getProviderDisplayLabel(
                  getProviderSlugFromContract(
                    contractSecretaryAgent,
                    "secretary_agent"
                  ) || provider
                )}
              />
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractSecretaryAgent ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: isOwner ? "error700.main" : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: isOwner ? "error700.main" : "neutral300.main",
                },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!isOwner}
              onClick={() => setIsCancelModalOpen(true)}
            >
              {isOwner ? "Cancelar Secretária IA" : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={handleOpenModal}
              startIcon={<SupportAgent />}
              disabled={!canActivate}
            >
              {isOwner ? "Ativar Secretária IA" : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>
      <ConfigureAIModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        identifier="SECRETARY_AGENT"
      />
      <CancelIntegrationModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleRemoveContract}
        agentName="Secretária IA"
      />
    </>
  );
}
